/* =========================================================================
   Color A Dog — theme.js
   ========================================================================= */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* -------- Mobile nav --------------------------------------------- */
  const menuToggle = $('.menu-toggle');
  const nav = $('.nav');
  if (menuToggle && nav) {
    menuToggle.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      menuToggle.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
    });
    nav.addEventListener('click', (e) => {
      if (e.target.tagName === 'A') {
        nav.classList.remove('is-open');
        menuToggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }
    });
  }

  /* -------- Reveal stagger indices -------------------------------- */
  /* CSS handles the animation; we just set a per-sibling index so the
     stagger feels intentional rather than uniform. */
  ['steps', 'before-after', 'kits', 'quotes__inner', 'faq'].forEach((parentClass) => {
    $$(`.${parentClass} .reveal`).forEach((el, i) => {
      el.style.setProperty('--rev-i', i);
    });
  });

  /* -------- Hero word-wrap (for staggered reveal) ------------------ */
  $$('.hero h1').forEach((h) => {
    if (h.dataset.wrapped) return;
    // Already wrapped by template; nothing to do.
    h.dataset.wrapped = '1';
  });

  /* -------- Mosaic painter ----------------------------------------- */
  /* Renders a dot-grid silhouette of a dog into a canvas using only the
     kit's actual marker palette. Two seeded variations, one per use. */
  const PALETTE = [
    '#e3552c', // vermilion
    '#f3b431', // marigold
    '#e89a2f', // saffron
    '#5a3a1e', // cocoa
    '#9b5e2d', // toast
    '#f7eddc', // bone (cream)
    '#2e3578', // indigo
    '#3f6a48', // pine
    '#d6716a', // rose
    '#2a1810', // ink (dark, for accents)
  ];

  // A simple stylized dog-head silhouette. Returns a function (x,y) -> 0..1
  // representing "darkness" of the original photo at that grid cell. We
  // approximate a dog head with overlapping ellipses for ears, snout, eyes.
  function makeDogField(seed = 0) {
    const r = mulberry32(seed * 9301 + 49297);
    // Skew/rotate per seed for variation
    const skew = (r() - 0.5) * 0.2;
    return function field(u, v) {
      // u, v in [0,1]
      const x = (u - 0.5) + (v - 0.5) * skew;
      const y = (v - 0.5);

      // Background: warm wash (low value)
      let dark = 0.05;

      // Head ellipse
      const head = ellipse(x, y + 0.05, 0.34, 0.32);
      dark = Math.max(dark, head * 0.95);

      // Snout (lighter, a smudge below)
      const snout = ellipse(x, y + 0.18, 0.18, 0.13) * 0.85;
      dark = Math.max(dark, snout);

      // Nose (dark accent)
      const nose = ellipse(x, y + 0.16, 0.05, 0.04);
      if (nose > 0.2) dark = Math.min(1, dark + nose * 1.2);

      // Ears — flopped, on the sides
      const earL = ellipse(x + 0.22 + 0.02 * Math.sin(seed), y - 0.05, 0.13, 0.22);
      const earR = ellipse(x - 0.22 - 0.02 * Math.sin(seed), y - 0.05, 0.13, 0.22);
      dark = Math.max(dark, earL * 0.95);
      dark = Math.max(dark, earR * 0.95);

      // Eyes (dark accents)
      const eyeL = ellipse(x + 0.10, y - 0.05, 0.03, 0.025);
      const eyeR = ellipse(x - 0.10, y - 0.05, 0.03, 0.025);
      if (eyeL > 0.1) dark = Math.min(1, dark + eyeL * 2);
      if (eyeR > 0.1) dark = Math.min(1, dark + eyeR * 2);

      // White blaze
      const blaze = ellipse(x, y - 0.02, 0.04, 0.18);
      if (blaze > 0.1) dark = Math.max(0.2, dark - blaze * 0.8);

      // Background corner glow / sunset
      const corner = Math.max(0, 1 - Math.hypot((u - 0.0) * 1.2, (v - 0.0) * 1.2));
      dark = Math.max(dark, corner * 0.25);

      // Texture noise
      const n = (r() * 0.0 + Math.sin((u * 91.7 + v * 53.3 + seed * 13.7) * 6.28) * 0.5 + 0.5) * 0.06;

      return clamp(dark + n, 0, 1);
    };
  }

  function ellipse(x, y, rx, ry) {
    const v = 1 - ((x * x) / (rx * rx) + (y * y) / (ry * ry));
    return v > 0 ? Math.min(1, v) : 0;
  }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function mulberry32(a) {
    return function() {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // Map "darkness" 0..1 to a palette index based on the dog field zones
  function pickColor(d, u, v, seed) {
    // Background "sunset" corona — uses warm tones
    const isFigure = d > 0.35;
    const r = (Math.sin((u * 27 + v * 13 + seed) * 6.28) + 1) * 0.5;

    if (!isFigure) {
      // Background: sunset palette — vermilion, marigold, saffron, with bone splatter
      if (r < 0.04) return 5;            // bone
      if (r < 0.30) return 0;            // vermilion
      if (r < 0.55) return 2;            // saffron
      if (r < 0.80) return 1;            // marigold
      return 0;                          // vermilion
    }
    // Figure: cocoa, toast, bone (white blaze), ink for nose/eye
    if (d > 0.85) return 9;              // ink (eyes/nose)
    if (d > 0.65) return 3;              // cocoa
    if (d > 0.45) return 4;              // toast
    if (d > 0.32) return 5;              // bone (blaze)
    return 4;                             // toast fallback
  }

  function renderMosaic(canvas, opts = {}) {
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = canvas.clientWidth || 600;
    const H = canvas.clientHeight || 600;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const cols = opts.cols || 56;
    const rows = opts.rows || 56;
    const seed = opts.seed || 1;
    const field = makeDogField(seed);

    const cellW = W / cols;
    const cellH = H / rows;
    const dotR = Math.min(cellW, cellH) * 0.42;

    // Background fill (very subtle warm wash)
    ctx.fillStyle = '#f7eddc';
    ctx.fillRect(0, 0, W, H);

    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        const u = (i + 0.5) / cols;
        const v = (j + 0.5) / rows;
        const d = field(u, v);
        const c = pickColor(d, u, v, seed);
        ctx.fillStyle = PALETTE[c];
        // Draw a circle (dot) with tiny size variation
        const wobble = 0.92 + ((Math.sin((i * 31.3 + j * 17.7 + seed) * 6.28) + 1) * 0.08);
        ctx.beginPath();
        ctx.arc(i * cellW + cellW / 2, j * cellH + cellH / 2, dotR * wobble, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  // Mosaic painter is retained for any future <canvas> use, but the hero
  // and product images now use real photos of completed kits.
  function paintMosaics() {
    const hero = document.getElementById('hero-mosaic');
    if (hero) renderMosaic(hero, { cols: 56, rows: 56, seed: 7 });
    const prod = document.getElementById('product-mosaic');
    if (prod) renderMosaic(prod, { cols: 64, rows: 64, seed: 13 });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintMosaics);
  } else {
    paintMosaics();
  }

  /* -------- Product image thumb swap ------------------------------ */
  const mainImg = $('.product-media img');
  if (mainImg) {
    $$('.product-thumb').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const thumbImg = $('img', thumb);
        if (thumbImg) {
          mainImg.src = thumbImg.src;
          mainImg.alt = thumb.getAttribute('aria-label') || mainImg.alt;
        }
        $$('.product-thumb').forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* -------- Product page: size swatch, price update, uploader ----- */
  const sizeRadios = $$('input[name="id"]');
  const priceEls = [$('#product-price'), $('#display-price')].filter(Boolean);
  if (sizeRadios.length && priceEls.length) {
    sizeRadios.forEach((r) => {
      r.addEventListener('change', () => {
        $$('.swatches').forEach((g) => {
          $$('input[name="id"]', g).forEach((rr) => {
            rr.closest('.swatch').classList.toggle('swatch--active', rr.checked);
          });
        });
        if (r.dataset.price) priceEls.forEach((el) => { el.textContent = r.dataset.price; });
      });
    });
  }
  // Generic radio swatch sync
  $$('.swatches').forEach((group) => {
    group.addEventListener('change', (e) => {
      $$('input[type="radio"]', group).forEach((r) => {
        r.closest('.swatch').classList.toggle('swatch--active', r.checked);
      });
    });
  });

  // Uploader: drag, file preview
  const uploader = $('#dog-uploader');
  if (uploader) {
    const input = $('input[type="file"]', uploader);
    const art = $('.uploader__art', uploader);
    const title = $('.uploader__title', uploader);
    const hint = $('.uploader__hint', uploader);

    function handleFile(file) {
      if (!file || !file.type.startsWith('image/')) return;
      uploader.classList.add('has-file');
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (art) {
          art.style.background = `url(${ev.target.result}) center/cover`;
          art.style.color = 'transparent';
          art.style.borderRadius = '12px';
        }
      };
      reader.readAsDataURL(file);
      title.textContent = file.name.replace(/\.[^.]+$/, '');
      hint.textContent = `${(file.size / 1024 / 1024).toFixed(1)} MB · Looks good. We'll send a proof in 48 hours.`;
    }

    input.addEventListener('change', (e) => handleFile(e.target.files[0]));

    ['dragenter', 'dragover'].forEach((evt) => {
      uploader.addEventListener(evt, (e) => {
        e.preventDefault();
        uploader.classList.add('is-drag');
      });
    });
    ['dragleave', 'drop'].forEach((evt) => {
      uploader.addEventListener(evt, (e) => {
        e.preventDefault();
        uploader.classList.remove('is-drag');
      });
    });
    uploader.addEventListener('drop', (e) => {
      const file = e.dataTransfer.files[0];
      if (file) {
        const dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
        handleFile(file);
      }
    });
  }

  /* -------- FAQ details a11y -------------------------------------- */
  // Native <details> already handles open/close. We only need to make sure
  // multiple details can be open and ensure smooth max-height feel.
  // (Skipping animation here on purpose — keeps it snappy.)

  /* -------- Cart flash on add ------------------------------------- */
  const flash = $('#cart-flash');
  function showFlash(msg = 'Added to bag.') {
    if (!flash) return;
    flash.textContent = msg;
    flash.classList.add('is-visible');
    clearTimeout(flash.__t);
    flash.__t = setTimeout(() => flash.classList.remove('is-visible'), 2400);
  }
  // Soft hook for AJAX add-to-cart implementations
  document.addEventListener('cart:added', () => showFlash('Added to bag.'));

  /* -------- Image preview modal ----------------------------------- */
  const imageModal = $('#image-modal');
  if (imageModal && typeof imageModal.showModal === 'function') {
    const modalImg = $('.image-modal__img', imageModal);
    const modalClose = $('.image-modal__close', imageModal);

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-zoom-src]');
      if (!trigger) return;
      e.preventDefault();
      modalImg.src = trigger.dataset.zoomSrc;
      modalImg.alt = trigger.dataset.zoomAlt || '';
      imageModal.showModal();
    });

    modalClose.addEventListener('click', () => imageModal.close());
    imageModal.addEventListener('click', (e) => {
      if (e.target === imageModal) imageModal.close();
    });
    imageModal.addEventListener('close', () => {
      modalImg.removeAttribute('src');
    });
  }

  /* -------- Quantity steppers ------------------------------------- */
  $$('.qty-stepper').forEach((stepper) => {
    const out = $('output', stepper);
    const buttons = $$('button', stepper);
    if (!out || buttons.length !== 2) return;
    buttons[0].addEventListener('click', () => {
      out.textContent = Math.max(1, parseInt(out.textContent, 10) - 1);
    });
    buttons[1].addEventListener('click', () => {
      out.textContent = parseInt(out.textContent, 10) + 1;
    });
  });

})();
